import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { Text } from '@atlaskit/primitives/compiled';
import { ModalHeader, ModalTitle, ModalBody, ModalFooter } from '@atlaskit/modal-dialog';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import { useContentTitles } from './hooks/useContentTitles.ts';
import { type Suggestion } from './types/RemoteObject.ts';

type ParentGrouping = {
	parentTitle: string;
	suggestions: Suggestion[];
};

type ReviewContentSuggestionsModalProps = {
	suggestions: Suggestion[];
	onClose: () => void;
};

const i18n = defineMessages({
	newSuggestionsAvailable: {
		id: 'side-navigation.review-content-suggestions-modal.new-suggestions-available',
		defaultMessage: 'New suggestions available',
		description: 'Title of the modal that opens to review content suggestions',
	},
	reviewContentSuggestions: {
		id: 'side-navigation.review-content-suggestions-modal.review-content-suggestions',
		defaultMessage:
			'We found {count, plural, one {# item} other {# items}} from your connected apps that might belong here.',
		description: 'Description of the modal that opens to review content suggestions',
	},
	closeButtonText: {
		id: 'side-navigation.review-content-suggestions-modal.close-button-text',
		defaultMessage: 'Close',
		description: 'Text for the close button in the modal that opens to review content suggestions',
	},
	untitled: {
		id: 'side-navigation.review-content-suggestions-modal.untitled',
		defaultMessage: 'Untitled',
		description: 'Fallback when parent content has no title',
	},
	groupWithCount: {
		id: 'side-navigation.review-content-suggestions-modal.group-with-count',
		defaultMessage: '{title} ({count})',
		description: 'Parent group label with suggestion count',
	},
});

const groupSuggestionsByParent = (suggestions: Suggestion[]) => {
	const groupedSuggestions = new Map<number, Suggestion[]>();

	suggestions.forEach((suggestion) => {
		if (groupedSuggestions.has(suggestion.parentId)) {
			groupedSuggestions.get(suggestion.parentId)?.push(suggestion);
		} else {
			groupedSuggestions.set(suggestion.parentId, [suggestion]);
		}
	});

	return groupedSuggestions;
};

export const ReviewContentSuggestionsModal = ({
	suggestions,
	onClose,
}: ReviewContentSuggestionsModalProps) => {
	const { formatMessage } = useIntl();
	const { Modal } = useChoreographerAssets();

	const groupedSuggestions = useMemo(() => groupSuggestionsByParent(suggestions), [suggestions]);

	const parentIds = useMemo(
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity
		() => [...new Set(suggestions.map((s) => String(s.parentId)))].filter(Boolean),
		[suggestions],
	);

	const titlesByContentId = useContentTitles(parentIds);

	const groupedSuggestionsWithTitles = useMemo(() => {
		const result = new Map<number, ParentGrouping>();
		groupedSuggestions.forEach((parentSuggestions, parentId) => {
			const parentTitle = titlesByContentId.get(String(parentId));
			if (parentTitle) {
				result.set(parentId, {
					parentTitle,
					suggestions: parentSuggestions,
				});
			}
		});
		return result;
	}, [groupedSuggestions, titlesByContentId]);

	return (
		<Modal onClose={onClose} width="x-large">
			<ModalHeader>
				<ModalTitle>{formatMessage(i18n.newSuggestionsAvailable)}</ModalTitle>
			</ModalHeader>
			<ModalBody>
				<Text>
					{formatMessage(i18n.reviewContentSuggestions, {
						count: suggestions.length,
					})}
				</Text>
				{/* TODO: replace with actual content suggestions list */}
				{[...groupedSuggestionsWithTitles.entries()].map(
					([parentId, { parentTitle, suggestions: groupSuggestions }]) => (
						<Text key={parentId} as="p">
							{formatMessage(i18n.groupWithCount, {
								title: parentTitle || formatMessage(i18n.untitled),
								count: groupSuggestions.length,
							})}
						</Text>
					),
				)}
			</ModalBody>
			<ModalFooter>
				<Button appearance="primary" onClick={onClose}>
					{formatMessage(i18n.closeButtonText)}
				</Button>
			</ModalFooter>
		</Modal>
	);
};
